import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { IDeliveryLine } from '@interfaces/ILogistics';
import { ChevronDownIcon } from '@utils/icons';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { lineAmount } from '../lines';
import { DeliverySources, DeliverySourcePill } from './DeliverySource';
import styles from './DeliveryLinesTable.module.css';

/**
 * The delivery drawer's materials table (design 2026-09-14 §4.3, shape ruled
 * 2026-09-15: option C) — one row per line, replacing the single Quantity
 * field an order used to have. Two columns, Material and Amount; what booked
 * the material and its "N tasks" chip sit under the name. A row with sources
 * expands to the "Raised from task" blocks the drawer already renders, on a
 * thin rail so they read as that row's.
 */
interface IDeliveryLinesTableProps {
  siteId: string;
  lines: IDeliveryLine[];
  /** What sits left of a line's amount — its material's stock action. */
  action: (line: IDeliveryLine) => ReactNode;
}

export function DeliveryLinesTable({ siteId, lines, action }: IDeliveryLinesTableProps) {
  const { t } = useTranslation();
  const catalog = useSiteLocations(siteId);
  const floorNames = new Map(catalog.floors.map((floor) => [floor.id, floor.name]));
  return (
    <div className={styles.table}>
      <div className={styles.headRow}>
        <span>{t('logistics.orderDialog.item')}</span>
        <span className={styles.amountCol}>{t('logistics.detail.amount')}</span>
      </div>
      {lines.map((line) => (
        <LineRow key={line.id} siteId={siteId} line={line} floorNames={floorNames} action={action(line)} t={t} />
      ))}
    </div>
  );
}

/**
 * What booked this material, in one line under its name: the element kinds
 * summed across the line's tasks, then the floors those claims name. Null for
 * a line nothing booked (raised straight from Logistics) or one whose tasks
 * are gone.
 */
function bookedSubline(
  line: IDeliveryLine,
  floorNames: ReadonlyMap<string, string>,
  t: TFunction,
): string | null {
  const counts = new Map<string, number>();
  const floors = new Set<string>();
  for (const source of line.sources) {
    const booked = source.task?.booked;
    if (!booked) continue;
    for (const el of booked.elements) counts.set(el.kind, (counts.get(el.kind) ?? 0) + el.count);
    for (const id of booked.floorIds) {
      const name = floorNames.get(id);
      if (name !== undefined) floors.add(name);
    }
  }
  const parts = [
    ...[...counts].map(([kind, count]) => t('logistics.source.bookedKind', { count, kind })),
    ...(floors.size > 0 ? [[...floors].join(t('common.listSeparator'))] : []),
  ];
  // The separator is structural punctuation, not copy — the card's meta line
  // uses the same literal " · ".
  return parts.length > 0 ? parts.join(' · ') : null;
}

interface ILineRowProps {
  siteId: string;
  line: IDeliveryLine;
  floorNames: ReadonlyMap<string, string>;
  action: ReactNode;
  t: TFunction;
}

function LineRow({ siteId, line, floorNames, action, t }: ILineRowProps) {
  const [open, setOpen] = useState(false);
  const sub = bookedSubline(line, floorNames, t);
  const amount = lineAmount(line, t) ?? t('logistics.detail.empty');
  const expandable = line.sources.length > 0;
  return (
    <div className={styles.lineBlock} data-testid="delivery-line">
      {/* Only a line with tasks behind it has anything to expand TO; the rest
          are plain names rather than buttons that do nothing. The toggle is
          the name alone: the action beside it is a button of its own. */}
      <div className={styles.row}>
        {expandable ? (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span className={styles.caret} data-collapsed={!open}>
              <ChevronDownIcon size={12} />
            </span>
            <LineName line={line} sub={sub} />
          </button>
        ) : (
          <LineName line={line} sub={sub} />
        )}
        {action}
        <span className={styles.amount}>{amount}</span>
      </div>
      {open ? (
        <div className={styles.rail}>
          <DeliverySources siteId={siteId} sources={line.sources} />
        </div>
      ) : null}
    </div>
  );
}

interface ILineNameProps {
  line: IDeliveryLine;
  sub: string | null;
}

function LineName({ line, sub }: ILineNameProps) {
  return (
    <span className={styles.name}>
      {line.itemName}
      {sub !== null || line.sources.length > 0 ? (
        <span className={styles.sub}>
          {sub}
          {line.sources.length > 0 ? <DeliverySourcePill sources={line.sources} withCount /> : null}
        </span>
      ) : null}
    </span>
  );
}
